import { useQuery } from "@tanstack/react-query";

import { useObjectUrl } from "@/shared/hooks/useObjectUrl";
import { usePdfAspectRatio } from "@/shared/hooks/usePdfAspectRatio";
import { SECTION_SPEC_MEDIA_TYPE } from "@/shared/models/SectionSpecUpload";
import { getTestShackReportImage } from "../../repositories/TestShackReportRepository";

interface UseTestShackReportDrawingPreviewQueryProps {
	uploadFileId: number | null;
	// Panel and dialog keep separate cache entries so one failure raises one error.
	scope?: "panel" | "dialog";
	enabled?: boolean;
	onError?: (error: Error) => void;
}

export const useTestShackReportDrawingPreviewQuery = ({
	uploadFileId,
	scope = "panel",
	enabled = true,
	onError,
}: UseTestShackReportDrawingPreviewQueryProps) => {
	const query = useQuery<Blob, Error>({
		queryKey: ["test-shack-report-image", scope, uploadFileId],
		queryFn: ({ signal }) => getTestShackReportImage(uploadFileId ?? 0, signal),
		enabled: enabled && uploadFileId !== null,
		cacheTime: 0,
		retry: false,
		refetchOnWindowFocus: false,
		onError,
	});
	const objectUrl = useObjectUrl(query.data ?? null);
	const isPdf = query.data?.type === SECTION_SPEC_MEDIA_TYPE.PDF;
	const aspectRatio = usePdfAspectRatio(
		uploadFileId,
		query.data ?? null,
		isPdf,
	);

	return { ...query, objectUrl, isPdf, aspectRatio };
};
